/**
 * A record's one picture: a person's photograph (decision 0616) and a company's
 * logo (T170), drawn by the banner's picture slot where one is set and stored as a
 * `File` tagged to the record on the Item image's own terms (decision 0269).
 * 🔨 Scaling only ever shrinks; a picture already inside the box is stored as it
 * came.
 */

/**
 * 512: twice the widest sensible rendering of a 48-pixel avatar on a retina
 * screen, and small enough that the bytes are never the reason a page is slow.
 */
export const RECORD_PICTURE_MAX_PX = 512;

/** The seeded FileType every person photo takes; the modal never asks for one. */
export const PERSON_PHOTO_FILE_TYPE_NAME = 'Person Photo';

/** The seeded FileType a company logo takes, on the same terms (T170). */
export const COMPANY_LOGO_FILE_TYPE_NAME = 'Company Logo';

/** The seeded FileType a Location's photograph takes, on the company logo's terms (decision 0667). */
export const LOCATION_PHOTO_FILE_TYPE_NAME = 'Location Photo';

export const RECORD_PICTURE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;

export function isRecordPictureMime(mimeType: string): boolean {
  return (RECORD_PICTURE_MIME_TYPES as readonly string[]).includes(mimeType);
}

/**
 * One line of the banner's contact column (decision 0616). `kind` decides what
 * the row draws: a phone row a green Call button, an email row a `mailto:` link,
 * a web row an external link — which is where a LinkedIn profile comes from,
 * there being no LinkedIn contact-method kind — and a plain row nothing to press.
 * 🔨 A fax is a plain row: dialing a fax from a browser reaches a tone.
 */
export type BannerContactKind = 'phone' | 'email' | 'web' | 'plain';

export interface BannerContactLine {
  kind: BannerContactKind;
  /** What the reader sees: the formatted number, the address, the URL as typed. */
  text: string;
  /** Where the row goes; empty on a plain row, which is not a link. */
  href: string;
  /** The contact-method kind the row came from, so the screen can name it. */
  label: string;
}

/** The picture slot's own answer: what to draw and whether this reader may change it. */
export interface BannerAvatar {
  /** The File holding the picture, or null where the record has none. */
  imageFileId: string | null;
  /** The palette token name the initials draw on, for a Person; null where the slot draws no initials. */
  avatarColor: string | null;
  /** True where the reader may set or clear the picture, which is what draws the camera badge. */
  editable: boolean;
}

/**
 * The `tel:` a Call button opens: every character a dialer cannot use is
 * dropped, and a leading plus is kept because it carries the country.
 */
export function telHref(value: string, extension?: string | null): string {
  const plus = value.trim().startsWith('+') ? '+' : '';
  const digits = value.replace(/\D/g, '');
  return `tel:${plus}${digits}${extension ? `;ext=${extension.replace(/\D/g, '')}` : ''}`;
}

/** A typed web address made absolute, so a value stored without a scheme still opens. */
export function webHref(value: string): string {
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * Whether an Other contact line is a web address rather than a note. 🔨 The test
 * is a dot inside a word with no space and no at-sign, which is what separates
 * `linkedin.com/in/alex-chen` from *"reception takes messages"*.
 */
export function isWebContactValue(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '' || /\s/.test(trimmed) || trimmed.includes('@')) return false;
  return /^https?:\/\//i.test(trimmed) || /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/|$)/i.test(trimmed);
}

/**
 * 🔒 The drag-to-place and zoom step the picture window takes after a file is
 * picked (decision 0624). *"how do you center or adjust the crop placement"* —
 * product owner, 2026-09-22. The window holds the placement, the wire carries
 * the crop, and the server cuts the square before it scales.
 */

/** The picture the window is placing, in its own pixels. */
export interface RecordPictureSource {
  width: number;
  height: number;
}

/**
 * Where the picture sits behind the round frame: the source point standing at
 * the frame's centre, and how far in it is zoomed. 🔨 The centre is held in
 * source pixels rather than in frame ones, so zooming keeps whatever is under
 * the middle of the frame under the middle of the frame.
 */
export interface RecordPicturePlacement {
  centreX: number;
  centreY: number;
  zoom: number;
}

/**
 * The square the server cuts, in the pixels of the picture the browser sent.
 * 🔨 It is a request field and never a column: what is stored is the result,
 * and re-editing a stored picture starts from the stored picture.
 */
export interface RecordPictureCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 1× fills the frame with the picture's short side; 3× is as far in as the window goes. */
export const RECORD_PICTURE_ZOOM_MIN = 1;
export const RECORD_PICTURE_ZOOM_MAX = 3;

function between(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return Math.min(Math.max(value, low), Math.max(low, high));
}

export function clampZoom(zoom: number): number {
  return between(zoom, RECORD_PICTURE_ZOOM_MIN, RECORD_PICTURE_ZOOM_MAX);
}

/** The side of the square the frame shows, in source pixels: the short side at 1×, less as it zooms in. */
export function cropSide(source: RecordPictureSource, zoom: number): number {
  return Math.min(source.width, source.height) / clampZoom(zoom);
}

/**
 * The placement with its centre pulled back inside the picture. 🔨 A drag past
 * the edge stops at the edge rather than leaving the circle part empty, which is
 * the one thing a crop step must not allow.
 */
export function clampPlacement(source: RecordPictureSource, placement: RecordPicturePlacement): RecordPicturePlacement {
  const zoom = clampZoom(placement.zoom);
  const half = cropSide(source, zoom) / 2;
  return {
    zoom,
    centreX: between(placement.centreX, half, source.width - half),
    centreY: between(placement.centreY, half, source.height - half),
  };
}

/** The window opens on the middle of the picture at 1×, which is what the circle drew before there was a crop step. */
export function centredPlacement(source: RecordPictureSource): RecordPicturePlacement {
  return { centreX: source.width / 2, centreY: source.height / 2, zoom: RECORD_PICTURE_ZOOM_MIN };
}

/** The crop a placement stands for, in whole source pixels and never reaching outside the picture. */
export function cropFor(source: RecordPictureSource, placement: RecordPicturePlacement): RecordPictureCrop {
  const held = clampPlacement(source, placement);
  const side = Math.max(1, Math.round(cropSide(source, held.zoom)));
  const x = Math.round(between(held.centreX - side / 2, 0, source.width - side));
  const y = Math.round(between(held.centreY - side / 2, 0, source.height - side));
  return { x, y, width: side, height: side };
}

/** Whether a crop lies whole inside the picture, which is what the server refuses on. */
export function cropInside(source: RecordPictureSource, crop: RecordPictureCrop): boolean {
  const whole = (value: number) => Number.isInteger(value);
  if (![crop.x, crop.y, crop.width, crop.height].every((value) => Number.isFinite(value) && whole(value))) return false;
  if (crop.width < 1 || crop.height < 1 || crop.x < 0 || crop.y < 0) return false;
  return crop.x + crop.width <= source.width && crop.y + crop.height <= source.height;
}
